import {
  DestinationType,
  FormData,
  IndexKey
} from 'pages/apps/project-management/types/types';
import { jobBoardAPI } from './base';
import { AxiosResponse } from 'axios';
import { JobStage } from './jobStage';
import { JobType } from './jobTypes';
import { RigResponse } from './rigs';
import { ContactPerson, Employee, JobClient } from 'data/users';
import { JobAttachment } from './file';
import { Location } from './location';
import { ToDoItem } from 'data/project-management/todoListData';
import { CommentType } from './comment';

const jobEndpoint = {
  create: '/job',
  getAll: '/job',
  getJob: '/job',
  getAllActivityLogs: '/job/job-activity-logs/all',
  getActivityLogsByJobId: '/job/job-activity-logs/by-job',
  getLatestPostJobReport: '/job/job-activity-logs/post-job-report',
  createActivityLog: '/job/job-activity-logs',
  searchJobs: '/job/search'
};
export interface UpdateJobStageDto {
  jobId: {
    _id: string;
  };
  stageId: string;
  userId: string;
  comment?: string;
  returnDate?: Date;
}
export interface CommentKey {
  id?: string;
  _id: string;
}

export interface JobComment extends CommentKey {
  content: string;
  jobId: string;
  creator: Employee;
  createDate?: number;
  createdAt?: Date;
  commentType?: CommentType;
  isSticky?: boolean;
  parentCommentId?: string;
  replies?: JobComment[];
}
export interface JobStageHistory {
  stage: {
    name: string;
    _id: string;
  };
  timestamp: Date;
}
export interface Job {
  id?: string;
  _id?: string;
  title: string;
  well: string;
  remark: string;
  stageHistory: JobStageHistory[];
  description: string;
  leavingFrom: string;
  jobNumber: string | null;
  returnDate: Date;
  departureDate: Date;
  expectedEquipmentShipDate: Date;
  destinationType: DestinationType;
  audit?: [string];
  rig: RigResponse;
  jobType: JobType;
  location: Location;
  customer: JobClient;
  jobStage: JobStage;
  comments?: JobComment[];
  tasks?: ToDoItem[];
  assignedTo: Employee[];
  supervisor: Employee[];
  files: JobAttachment[];
  isPriority: boolean;
  lastActiveJobStage: JobStage;
  customerRepresentative: ContactPerson;
  toolWorkOrders?: any[];
  watchers?: string[];
}
export interface JobWithMetadata extends Job {
  daysReturned: number;
  isPriority: boolean;
}

export interface JobStageDto {
  jobId: string;
  jobStage: IndexKey;
}

export interface JobPriorityDto {
  jobId: string;
  priority: boolean;
}

// Activity Log interfaces
export interface ActivityLogUser {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
}

export interface ActivityLogJob {
  _id: string;
  title: string;
  jobNumber: string;
}

export interface ActivityLogMetadata {
  oldPriority?: boolean;
  newPriority?: boolean;
  oldStageId?:
    | {
        _id: string;
      }
    | string;
  newStageId?: string;
}

export interface ActivityLog {
  _id: string;
  jobId: ActivityLogJob;
  userId: ActivityLogUser;
  activityType: string;
  description: string;
  metadata: ActivityLogMetadata;
  createdAt: string;
  updatedAt: string;
}

export interface CreateJobActivityLogPayload {
  jobId: string;
  activityType: string;
  description: string;
  metadata?: Record<string, any>;
}

export const CREATE_JOB = async (formData: FormData): Promise<any> => {
  return jobBoardAPI.post(jobEndpoint.create, formData);
};

export const GET_JOB_QUERY = async (
  jobId: string
): Promise<AxiosResponse<Job>> => {
  return (await jobBoardAPI.get(`${jobEndpoint.getJob}/${jobId}`)).data;
};

export const GET_JOBS_QUERY = async (): Promise<AxiosResponse<Job[]>> => {
  return (await jobBoardAPI.get(jobEndpoint.getAll)).data;
};

export const CREATE_JOB_ACTIVITY_LOG = async (
  payload: CreateJobActivityLogPayload
) => {
  return jobBoardAPI.post(jobEndpoint.createActivityLog, payload);
};

export const UPDATE_JOB = async ({
  formData,
  jobId
}: {
  formData: FormData;
  jobId: string;
}): Promise<any> => {
  return jobBoardAPI.patch(`${jobEndpoint.create}/${jobId}`, formData);
};

export const UPDATE_JOB_STAGE = async (
  jobStageDto: JobStageDto
): Promise<any> => {
  return jobBoardAPI.patch(
    `${jobEndpoint.create}/${jobStageDto.jobId}`,
    jobStageDto
  );
};

export const UPDATE_ARCHIVE_JOB_STAGE = async (
  jobStageDto: JobStageDto
): Promise<any> => {
  return jobBoardAPI.patch(`${jobEndpoint.create}/${jobStageDto.jobId}`, {
    jobStage: jobStageDto.jobStage
  });
};

export const UPDATE_JOB_PRIORITY = async (
  jobPriorityDto: JobPriorityDto
): Promise<any> => {
  return jobBoardAPI.post(
    `${jobEndpoint.create}/change-priority`,
    jobPriorityDto
  );
};

export const DELETE_JOB = async (id: string): Promise<any> => {
  return jobBoardAPI.delete(`${jobEndpoint.create}/${id}`);
};

export const ASSIGN_JOB = async ({
  userIds,
  id
}: {
  userIds: Array<string>;
  id: string;
}): Promise<any> => {
  return jobBoardAPI.post(`${jobEndpoint.create}/assign/${id}`, userIds);
};

export const UNASSIGN_JOB = async ({
  userIds,
  id
}: {
  userIds: string | Array<string>;
  id: string;
}): Promise<any> => {
  const data = Array.isArray(userIds) ? userIds : [userIds];
  return jobBoardAPI.post(`${jobEndpoint.create}/unassign/${id}`, data);
};

export const UPDATE_JOB_DESCRIPTION = async ({
  formData,
  jobId
}: {
  formData: { description: string };
  jobId: string;
}): Promise<any> => {
  return jobBoardAPI.patch(`${jobEndpoint.create}/${jobId}`, formData);
};

export const updateJobStage = async (data: UpdateJobStageDto) => {
  const response = jobBoardAPI.post(`${jobEndpoint.create}/stage`, data);
  if (!(await response).status) {
    throw new Error('Failed to update job stage');
  }
  return response;
};

export const getAllActivityLogs = async (): Promise<any> => {
  const response = await jobBoardAPI.get(jobEndpoint.getAllActivityLogs);
  return response.data;
};

export const getActivityLogsByJobId = async (jobId: string): Promise<any> => {
  const response = await jobBoardAPI.get(
    `${jobEndpoint.getActivityLogsByJobId}/${jobId}`
  );
  return response.data;
};

export const GET_LATEST_POST_JOB_REPORT = async (
  jobId: string
): Promise<any> => {
  const response = await jobBoardAPI.get(
    `${jobEndpoint.getLatestPostJobReport}/${jobId}`
  );
  return response.data;
};

export const SEARCH_JOBS = async (
  query: string
): Promise<AxiosResponse<Job[]>> => {
  const response = await jobBoardAPI.post(jobEndpoint.searchJobs, { query });
  return response.data;
};

export const TOGGLE_WATCH_JOB = async (
  jobId: string
): Promise<{ watching: boolean }> => {
  const response = await jobBoardAPI.post(`/job/${jobId}/watch`);
  return response.data?.data ?? response.data;
};
